import React from "react";
import {
  ArrowRight,
  Brain,
  ChevronLeft,
  ChevronRight,
  HeartPulse,
  MapPin,
  Trophy
} from "lucide-react";

// reactstrap components
import {
  Container,
  Row,
  Col
} from "reactstrap";

// core components
import MainNavbar from "components/Navbars/MainNavbar.js";
import DefaultFooter from "components/Footers/DefaultFooter.js";
import SEO from "components/SEO/SEO.js";
import accomplishmentYears from "data/accomplishments.js";

const heroVideo = require("../assets/img/endgame-home-sm4.mp4");
const heroPoster = require("../assets/img/header-nimona.jpg");
const agilityIcon = require("../assets/img/icon-agility.jpg");
const barnHuntIcon = require("../assets/img/icon-barnhunt.jpg");
const discIcon = require("../assets/img/icon-disc.jpg");
const dockDivingIcon = require("../assets/img/icon-dockdiving.jpg");
const fastCatIcon = require("../assets/img/icon-fastcat.jpg");
const flyballIcon = require("../assets/img/icon-flyball.jpg");
const herdingIcon = require("../assets/img/icon-herding.jpg");
const rallyIcon = require("../assets/img/icon-rally.jpg");

const offspringSlides = [
  {
    image: require("../assets/img/home-offspring3.jpg"),
    alt: "Endgame offspring competing in agility"
  },
  {
    image: require("../assets/img/home-offspring6.jpg"),
    alt: "Endgame offspring competing in agility"
  },
  {
    image: require("../assets/img/home-offspring1.jpg"),
    alt: "Endgame offspring running flyball"
  },
  {
    image: require("../assets/img/home-offspring5.jpg"),
    alt: "Endgame offspring running flyball"
  },
  {
    image: require("../assets/img/home-offspring4.jpg"),
    alt: "Endgame offspring dock diving"
  },
  {
    image: require("../assets/img/home-offspring7.jpg"),
    alt: "Endgame offspring dock diving"
  },
  {
    image: require("../assets/img/home-offspring2.jpg"),
    alt: "Endgame offspring playing disc"
  }
];

const valueSummaries = [
  {
    icon: Trophy,
    title: "Proven Performance",
    text: "Demonstrating national success across multiple dog sports and generations."
  },
  {
    icon: Brain,
    title: "Thoughtful Selection",
    text: "Each pairing is chosen to complement strengths and balance traits."
  },
  {
    icon: HeartPulse,
    title: "Comprehensive Screening",
    text: "Our dogs are health tested beyond minimum breed recommendations."
  }
];

const featuredDogs = [
  {
    name: "Venus",
    href: "/dogs/venus",
    image: require("../assets/img/home-venus.jpg"),
    imageAlt: "Venus, a smooth coat Border Collie, jumping in dog sports training",
    breed: "Border Collie",
    specialties: ["Flyball", "Dock Diving", "Herding"],
    highlightLabel: "Flyball PB",
    highlightValue: "3.764 sec"
  },
  {
    name: "Nimona",
    href: "/dogs/nimona",
    image: require("../assets/img/home-nim.jpg"),
    imageAlt: "Nimona, a Border Collie Jack Russell sport mix, competing in agility",
    breed: "Sport Mix",
    specialties: ["Agility", "Flyball", "Dock Diving", "Disc"],
    highlightLabel: "2026 UPDIF Midi",
    highlightValue: "Top 4%"
  },
  {
    name: "Rumi",
    href: "/dogs/rumi",
    image: require("../assets/img/home-rumi.jpg"),
    imageAlt: "Rumi, a young sport mix, dock diving",
    breed: "Sport Mix",
    specialties: ["Agility", "Flyball", "Dock Diving"],
    highlightLabel: "Dock Diving PB",
    highlightValue: "21'10\""
  }
];

const sports = [
  { name: "Agility", icon: agilityIcon },
  { name: "Flyball", icon: flyballIcon },
  { name: "Dock Diving", icon: dockDivingIcon },
  { name: "Disc", icon: discIcon },
  { name: "Rally", icon: rallyIcon },
  { name: "Barn Hunt", icon: barnHuntIcon },
  { name: "Fast CAT", icon: fastCatIcon },
  { name: "Herding", icon: herdingIcon }
];

const recentAccomplishments = accomplishmentYears
  .flatMap(accomplishmentYear =>
    accomplishmentYear.entries.map(entry => ({
      ...entry,
      year: accomplishmentYear.year
    }))
  )
  .filter(entry => entry.showOnHomepage)
  .slice(0, 4);

function useReducedMotionPreference() {
  const [prefersReducedMotion, setPrefersReducedMotion] = React.useState(false);

  React.useEffect(() => {
    if (!window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updatePreference = () => setPrefersReducedMotion(mediaQuery.matches);
    updatePreference();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", updatePreference);
      return () => mediaQuery.removeEventListener("change", updatePreference);
    }

    mediaQuery.addListener(updatePreference);
    return () => mediaQuery.removeListener(updatePreference);
  }, []);

  return prefersReducedMotion;
}

function Index() {
  const videoRef = React.useRef(null);
  const prefersReducedMotion = useReducedMotionPreference();

  React.useEffect(() => {
    document.body.classList.add("landing-page");
    document.body.classList.add("sidebar-collapse");
    document.documentElement.classList.remove("nav-open");
    return function cleanup() {
      document.body.classList.remove("landing-page");
      document.body.classList.remove("sidebar-collapse");
    };
  }, []);

  React.useEffect(() => {
    if (prefersReducedMotion && videoRef.current) {
      videoRef.current.pause();
    }
  }, [prefersReducedMotion]);

  return (
    <>
      <SEO
        title="Endgame Sport Dogs | Performance Border Collies in North Carolina"
        description="Endgame Sport Dogs breeds healthy, versatile Border Collies for agility, flyball, dock diving, disc, herding, and active homes in North Carolina."
        path="/"
        appendSiteName={false}
      />
      <MainNavbar transparent />
      <main className="home-page">
        <section className="home-hero" aria-label="Endgame Sport Dogs homepage introduction">
          <div className="home-hero-video-wrap" aria-hidden="true">
            <video
              ref={videoRef}
              className="home-hero-video"
              autoPlay={!prefersReducedMotion}
              loop
              muted
              playsInline
              preload="auto"
              poster={heroPoster}
            >
              <source src={heroVideo} type="video/mp4"></source>
            </video>
            <div className="home-hero-video-gradient"></div>
          </div>
          <div className="home-hero-overlay"></div>
          <Container className="home-hero-content">
            <div className="home-hero-copy">
              <h1>Purpose-Bred Dogs for Competitive Dog Sports</h1>
              <p>
                Endgame Sport Dogs is a small Border Collie and Sport Mix kennel focused on breeding versatile dogs for performance sports. Our dogs compete in agility, flyball, dock diving, disc, rally, herding, and more.
              </p>
              <div className="home-hero-actions">
                <HomeButton href="/dogs">Our Dogs</HomeButton>
                <HomeButton href="/litters" variant="secondary">View Litters</HomeButton>
              </div>
              <div className="home-location">
                <MapPin aria-hidden="true" size={18} strokeWidth={2.25} />
                <span>Located near Raleigh, North Carolina</span>
              </div>
            </div>
          </Container>
        </section>

        <ValueSummarySection />
        <FeaturedDogsSection />
        <SportsSection />
        <AccomplishmentsSection />
        <ContactCta />
      </main>
      <DefaultFooter variant="dark" />
    </>
  );
}

function ValueSummarySection() {
  return (
    <section className="home-values" aria-label="Endgame Sport Dogs breeding priorities">
      <Container>
        <Row>
          {valueSummaries.map(value => {
            const Icon = value.icon;

            return (
              <Col md="4" className="home-value" key={value.title}>
                <Icon aria-hidden="true" size={32} strokeWidth={2} />
                <h3>{value.title}</h3>
                <p>{value.text}</p>
              </Col>
            );
          })}
        </Row>
      </Container>
    </section>
  );
}

function HomeButton({ children, href, variant = "primary" }) {
  return (
    <a className={`btn btn-round home-btn home-btn-${variant}`} href={href}>
      <span>{children}</span>
      <ArrowRight aria-hidden="true" size={18} strokeWidth={2.25} />
    </a>
  );
}

function SectionHeading({ children, className = "" }) {
  return (
    <div className={`home-section-heading ${className}`.trim()}>
      <h2>{children}</h2>
      <span aria-hidden="true"></span>
    </div>
  );
}

function FeaturedDogsSection() {
  return (
    <section className="home-section home-featured-dogs site-bg-light-grey">
      <Container>
        <SectionHeading>Meet Some of Our Dogs</SectionHeading>
        <div className="home-dog-profiles">
          {featuredDogs.map(dog => (
            <article className="home-dog-profile" key={dog.href}>
              <a
                aria-label={`View ${dog.name}'s dog profile`}
                className="home-dog-profile-image-link"
                href={dog.href}
              >
                <img alt={dog.imageAlt} src={dog.image} />
              </a>
              <div className="home-dog-profile-content">
                <a className="home-dog-profile-name" href={dog.href}>
                  {dog.name}
                </a>
                <p className="home-dog-profile-breed">{dog.breed}</p>
                <span className="home-dog-profile-rule" aria-hidden="true"></span>
                <span className="home-dog-profile-specialties-label">Specialties</span>
                <p className="home-dog-profile-specialties">
                  {dog.specialties.map((specialty, index) => (
                    <React.Fragment key={specialty}>
                      {index > 0 && <span className="home-dog-specialty-separator"> • </span>}
                      <span>{specialty}</span>
                    </React.Fragment>
                  ))}
                </p>
                <div className="home-dog-profile-highlight">
                  <span>{dog.highlightLabel}</span>
                  <strong>{dog.highlightValue}</strong>
                </div>
              </div>
            </article>
          ))}
        </div>
        <div className="text-center">
          <HomeButton href="/dogs">View All Dogs</HomeButton>
        </div>
      </Container>
    </section>
  );
}

function SportsSection() {
  return (
    <section className="home-section home-sports">
      <Container>
        <SectionHeading className="home-section-heading-compact">Competing in a variety of sports</SectionHeading>
        <ul>
          {sports.map(sport => (
            <li key={sport.name}>
              <img alt="" aria-hidden="true" src={sport.icon} />
              {sport.name}
            </li>
          ))}
        </ul>
      </Container>
    </section>
  );
}

function AccomplishmentsSection() {
  const prefersReducedMotion = useReducedMotionPreference();
  const [activeSlide, setActiveSlide] = React.useState(0);
  const [isPaused, setIsPaused] = React.useState(false);
  const slideCount = offspringSlides.length;

  React.useEffect(() => {
    const nextSlide = offspringSlides[(activeSlide + 1) % slideCount];
    const image = new Image();
    image.src = nextSlide.image;
  }, [activeSlide, slideCount]);

  React.useEffect(() => {
    if (prefersReducedMotion || isPaused) {
      return undefined;
    }

    const slideTimer = window.setInterval(() => {
      setActiveSlide(currentSlide => (currentSlide + 1) % slideCount);
    }, 8500);

    return () => window.clearInterval(slideTimer);
  }, [isPaused, prefersReducedMotion, slideCount]);

  const showPreviousSlide = () => {
    setActiveSlide(currentSlide => (currentSlide - 1 + slideCount) % slideCount);
  };

  const showNextSlide = () => {
    setActiveSlide(currentSlide => (currentSlide + 1) % slideCount);
  };

  const selectSlide = slideIndex => {
    setActiveSlide(slideIndex);
  };

  return (
    <section className="home-offspring-action site-bg-dark-navy" aria-labelledby="offspring-action-heading">
      <div
        className={`home-offspring-feature${prefersReducedMotion ? " is-reduced-motion" : ""}`}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <div className="home-offspring-background-slides" aria-hidden="true">
          {offspringSlides.map((slide, index) => (
            <div className={`home-offspring-slide${index === activeSlide ? " active" : ""}`} key={slide.image}>
              <img alt="" src={slide.image} />
            </div>
          ))}
        </div>
        <div className="home-offspring-overlay"></div>

        <Container className="home-offspring-inner">
          <div className="home-offspring-stage">
            <div className="home-offspring-content">
              <h2 id="offspring-action-heading">Offspring in Action</h2>
              <span aria-hidden="true"></span>
              <p>
                Dogs from the Endgame program are competing in agility, flyball, dock diving, disc, and more.
              </p>

              <div className="home-offspring-nav">
                <div className="home-offspring-controls">
                  <button type="button" onClick={showPreviousSlide} aria-label="Previous offspring photo">
                    <ChevronLeft aria-hidden="true" size={22} strokeWidth={2.25} />
                  </button>
                  <button type="button" onClick={showNextSlide} aria-label="Next offspring photo">
                    <ChevronRight aria-hidden="true" size={22} strokeWidth={2.25} />
                  </button>
                </div>

                <div className="home-offspring-dots" aria-label="Offspring photo slideshow">
                  {offspringSlides.map((slide, index) => (
                    <button
                      aria-label={`Show offspring photo ${index + 1}`}
                      aria-pressed={index === activeSlide}
                      className={index === activeSlide ? "active" : ""}
                      key={slide.image}
                      onClick={() => selectSlide(index)}
                      type="button"
                    ></button>
                  ))}
                </div>
              </div>
            </div>

            <div className="home-offspring-foreground-slides">
              {offspringSlides.map((slide, index) => (
                <div
                  aria-hidden={index !== activeSlide}
                  className={`home-offspring-foreground-slide${index === activeSlide ? " active" : ""}`}
                  key={slide.image}
                >
                  <img
                    alt={index === activeSlide ? slide.alt : ""}
                    className="home-offspring-foreground-image"
                    src={slide.image}
                  />
                  <span aria-hidden="true" className="home-offspring-image-feather"></span>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </div>

      <div className="home-highlights-section site-bg-light-grey">
        <Container>
          <div className="home-highlights-strip">
            <div className="home-highlights-intro">
              <Trophy
                aria-hidden="true"
                className="home-highlights-intro-icon"
                size={28}
                strokeWidth={2}
              />
              <h3>Sport Highlights</h3>
              <span aria-hidden="true"></span>
              <p>Recent accomplishments from Endgame dogs and their offspring.</p>
              <a className="home-highlights-link" href="/about">
                <span>View All Accomplishments</span>
                <ArrowRight aria-hidden="true" size={16} strokeWidth={2.25} />
              </a>
            </div>
            <div className="home-highlight-items">
              {recentAccomplishments.map((accomplishment, index) => (
                <article
                  className={`home-highlight-item${index === 0 ? " home-highlight-item-featured" : ""}`}
                  key={`${accomplishment.names}-${accomplishment.text}`}
                >
                  {accomplishment.icon && (
                    <span className="home-highlight-icon" aria-hidden="true">
                      <img alt="" src={accomplishment.icon} />
                    </span>
                  )}
                  <div className="home-highlight-copy">
                    <h3>{accomplishment.homepageText || accomplishment.text}</h3>
                    <p>{accomplishment.names}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </Container>
      </div>
    </section>
  );
}

function ContactCta() {
  return (
    <section className="home-contact-cta site-bg-dark-navy">
      <Container>
        <div>
          <h2>Interested in an Endgame puppy?</h2>
          <p>
            We'd love to hear from you. Reach out to learn more about our program, upcoming litters, or puppy inquiries.
          </p>
        </div>
        <HomeButton href="/contact">Contact Us</HomeButton>
      </Container>
    </section>
  );
}

export default Index;
